import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { get } from "@/lib/api";
import { currentMonth, eur, hours, monthLabel, shiftMonth } from "@/lib/format";
import type { MonthSummary } from "@/lib/types";
import { deltaPct } from "./chart";

/** "Wie ist der Monat gelaufen?" — totals, change vs. previous month, split per customer. */
export function MonthBreakdown({ month, onMonth }: { month: string; onMonth: (m: string) => void }) {
  const q = (m: string) => ({ queryKey: ["summary", m], queryFn: () => get<MonthSummary>(`/api/summary?month=${m}`) });
  const cur = useQuery(q(month));
  const prev = useQuery(q(shiftMonth(month, -1)));
  const s = cur.data;
  const delta = s && prev.data ? deltaPct(s.amountEur, prev.data.amountEur) : null;
  const clients = [...(s?.perClient ?? [])].filter((c) => c.amountEur > 0 || c.billableMinutes > 0).sort((a, b) => b.amountEur - a.amountEur);
  const top = clients[0]?.amountEur ?? 0;

  return (
    <section aria-labelledby="month-title" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="month-title" className="text-base font-semibold">Monatsauswertung</h2>
        <div className="ml-auto flex items-center rounded-md border">
          <Button variant="ghost" size="icon-sm" aria-label="Vorheriger Monat" onClick={() => onMonth(shiftMonth(month, -1))}><ChevronLeft /></Button>
          <span className="w-32 text-center text-sm tabular-nums" aria-live="polite">{monthLabel(month)}</span>
          <Button variant="ghost" size="icon-sm" aria-label="Nächster Monat" disabled={month >= currentMonth()} onClick={() => onMonth(shiftMonth(month, 1))}><ChevronRight /></Button>
        </div>
      </div>

      <dl className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div><dt className="text-muted-foreground">Betrag</dt><dd className="text-lg font-semibold tabular-nums">{s ? eur(s.amountEur) : "–"}</dd></div>
        <div><dt className="text-muted-foreground">Fakturierbar</dt><dd className="text-lg tabular-nums">{s ? hours(s.billableMinutes) : "–"}</dd></div>
        <div>
          <dt className="text-muted-foreground">zu {monthLabel(shiftMonth(month, -1)).split(" ")[0]}</dt>
          <dd className="text-lg tabular-nums text-muted-foreground">{delta === null ? "–" : `${delta >= 0 ? "+" : "−"}${Math.abs(delta)} %`}</dd>
        </div>
      </dl>

      {s && clients.length === 0 && <p className="text-sm text-muted-foreground">Keine Buchungen im {monthLabel(month)}.</p>}
      <ul className="space-y-2.5">
        {clients.map((c) => (
          <li key={c.clientId} className="grid grid-cols-[minmax(0,12rem)_1fr_auto] items-center gap-3 text-sm max-sm:grid-cols-[1fr_auto]">
            <span className="truncate" title={c.name}>{c.name}</span>
            <div aria-hidden className="h-2 rounded-full bg-muted max-sm:col-span-2 max-sm:row-start-2">
              <div className="h-full rounded-full bg-chart-1" style={{ width: `${top > 0 ? Math.max(2, (c.amountEur / top) * 100) : 0}%` }} />
            </div>
            <span className="text-right tabular-nums text-muted-foreground max-sm:row-start-1 max-sm:col-start-2">
              {hours(c.billableMinutes)} · <span className="text-foreground">{eur(c.amountEur)}</span>
            </span>
          </li>
        ))}
      </ul>

      <Link to={`/buchungen?month=${month}&status=all`} className="inline-block text-sm text-muted-foreground hover:text-foreground">
        Alle Buchungen im {monthLabel(month)} →
      </Link>
    </section>
  );
}
